import { useState, type FormEvent } from 'react'
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../store/AuthContext'
import { errorMessage } from '../api/client'
import { setLanguage } from '../i18n'
import Keycap from '../components/Keycap'
import { c, chrome } from '../theme'

/**
 * What the ink panel states.
 *
 * It used to show sample balances -- Receivables 248,930 and so on. That
 * reads to a visitor as somebody's actual books: either real money that
 * should not be on a sign-in page, or obviously invented, and neither
 * inspires confidence in an accounting product. These are capabilities
 * instead, which is what the panel is actually for.
 *
 * `combo` renders as a keycap; the keyboard flow is the product's
 * strongest claim, so it leads.
 */
const CAPABILITIES: { label: string; detail: string; combo?: string }[] = [
  { label: 'Keyboard-first entry',
    detail: 'a full voucher without the mouse', combo: 'alt+8' },
  { label: 'Bill-wise receivables',
    detail: 'ageing to 90+ days, settled invoice by invoice' },
  { label: 'Saudi tax invoices',
    detail: '15% VAT, Hijri dates, bilingual letterhead' },
  { label: 'Immutable ledger',
    detail: 'corrections by reversal, never by edit' },
]

export default function LoginPage() {
  const { login } = useAuth()
  const { t, i18n } = useTranslation()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null); setBusy(true)
    try { await login(username, password) }
    catch (err) { setError(errorMessage(err)) }
    finally { setBusy(false) }
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid',
               gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
      {/* Ink panel: the ledger rule motif, stated once. */}
      <Box sx={{
        display: { xs: 'none', md: 'flex' }, flexDirection: 'column',
        justifyContent: 'space-between', bgcolor: chrome.bg, p: 6, color: chrome.text,
      }}>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography sx={{ fontWeight: 700, fontSize: 18, letterSpacing: '-0.02em' }}>
            {t('app.name')}
          </Typography>
          <Box sx={{ width: 6, height: 6, borderRadius: 6, bgcolor: chrome.accent }} />
        </Stack>

        <Box>
          <Typography sx={{
            fontSize: 30, fontWeight: 600, lineHeight: 1.25,
            letterSpacing: '-0.025em', maxWidth: 380,
          }}>
            {t('auth.tagline')}
          </Typography>

          <Box sx={{ mt: 4, maxWidth: 400 }}>
            {CAPABILITIES.map(({ label, detail, combo }) => (
              <Stack key={label} direction="row" alignItems="baseline" spacing={1.5}
                sx={{ py: 1.4, borderTop: `1px solid ${chrome.line}` }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, color: chrome.text, fontWeight: 500 }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, color: chrome.muted, mt: 0.3 }}>
                    {detail}
                  </Typography>
                </Box>
                {combo && <Keycap combo={combo} dark />}
              </Stack>
            ))}
          </Box>
        </Box>

        <Typography sx={{ fontSize: 12, color: chrome.muted }}>
          {t('auth.footnote')}
        </Typography>
      </Box>

      {/* Form */}
      <Box sx={{ display: 'grid', placeItems: 'center', p: 3 }}>
        <Box sx={{ width: 340, maxWidth: '100%' }}>
          {/* The wordmark only appears here on narrow screens, where the
              ink panel is hidden and nothing else identifies the product. */}
          <Stack direction="row" alignItems="baseline" spacing={1}
            sx={{ mb: 4, display: { xs: 'flex', md: 'none' } }}>
            <Typography sx={{ fontWeight: 700, fontSize: 18,
                              letterSpacing: '-0.02em', color: c.ink }}>
              {t('app.name')}
            </Typography>
            <Box sx={{ width: 6, height: 6, borderRadius: 6, bgcolor: c.primary }} />
          </Stack>

          <Typography variant="h6" sx={{ mb: 0.5 }}>{t('auth.signIn')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('auth.subtitle')}
          </Typography>

          {typeof navigator !== 'undefined' && !navigator.onLine
            && !window.location.pathname.startsWith('/van') && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('van.officeOffline')}{' '}<a href="/van">{t('van.openVan')}</a></Alert>
          )}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <form onSubmit={submit}>
            <Stack spacing={2}>
              <TextField label={t('auth.username')} value={username} autoFocus
                autoComplete="username" onChange={(e) => setUsername(e.target.value)} />
              <TextField label={t('auth.password')} type="password" value={password}
                autoComplete="current-password"
                onChange={(e) => setPassword(e.target.value)} />
              <Button type="submit" variant="contained" size="large"
                disabled={busy || !username || !password}>
                {busy ? t('common.loading') : t('auth.signIn')}
              </Button>
            </Stack>
          </form>

          <Button size="small" sx={{ mt: 2, color: 'text.secondary' }}
            onClick={() => setLanguage(i18n.language === 'ar' ? 'en' : 'ar')}>
            {i18n.language === 'ar' ? 'English' : 'العربية'}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
